import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../utils/api';
import { logger } from '../../utils/logger';

const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;
// Must match the backend cap (MAX_EMAILS_PER_BATCH): the Stripe SDK is
// synchronous server-side, so larger lists are sent as sequential chunks.
const BATCH_SIZE = 100;

type CouponDuration = 'once' | 'repeating' | 'forever';

interface CodeResult {
  email: string;
  code: string | null;
  promotionCodeId: string | null;
  status: 'created' | 'failed';
  error?: string | null;
  // Stamped client-side from the chunk's response so the admin can find
  // (and deactivate) the underlying Stripe coupon(s) later.
  couponId?: string;
}

interface BulkCouponResponse {
  couponId: string;
  results: CodeResult[];
}

export function parseEmails(text: string): { valid: string[]; invalidCount: number } {
  const tokens = text
    .split(/[\s,;]+/)
    .map((token) => token.trim())
    .filter(Boolean);
  const seen = new Set<string>();
  const valid: string[] = [];
  let invalidCount = 0;
  for (const token of tokens) {
    const normalized = token.toLowerCase();
    if (!EMAIL_RE.test(normalized)) {
      invalidCount += 1;
    } else if (!seen.has(normalized)) {
      seen.add(normalized);
      valid.push(normalized);
    }
  }
  return { valid, invalidCount };
}

// Quote every field and neutralize spreadsheet formula injection: a value
// starting with = + - @ (or tab/CR) would otherwise execute when the CSV is
// opened in Excel/Sheets.
export function escapeCsvField(value: string): string {
  const neutralized = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return `"${neutralized.replace(/"/g, '""')}"`;
}

export function buildCouponCsv(results: CodeResult[]): string {
  const header = 'email,code,status,couponId,error';
  const rows = results.map((r) =>
    [r.email, r.code ?? '', r.status, r.couponId ?? '', r.error ?? '']
      .map(escapeCsvField)
      .join(',')
  );
  return [header, ...rows].join('\n');
}

function readFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

export default function CouponsTab() {
  const [emails, setEmails] = useState<string[]>([]);
  const [invalidCount, setInvalidCount] = useState(0);
  const [fileName, setFileName] = useState<string | null>(null);
  const [percentOff, setPercentOff] = useState(25);
  const [duration, setDuration] = useState<CouponDuration>('once');
  const [durationInMonths, setDurationInMonths] = useState(3);
  const [couponName, setCouponName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [results, setResults] = useState<CodeResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  // One batch id per unique (file, settings) submission: retrying after a
  // failure reuses it, so the backend's Stripe idempotency keys prevent
  // duplicate coupons/codes.
  const batchRef = useRef<{ signature: string; id: string } | null>(null);

  const batchIdFor = (signature: string): string => {
    if (!batchRef.current || batchRef.current.signature !== signature) {
      batchRef.current = { signature, id: crypto.randomUUID() };
    }
    return batchRef.current.id;
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;
    try {
      const text = await readFileText(file);
      const parsed = parseEmails(text);
      setEmails(parsed.valid);
      setInvalidCount(parsed.invalidCount);
      setFileName(file.name);
      setResults([]);
      setError(null);
    } catch (err) {
      logger.error('Failed to read email list file', err);
      setError('Could not read that file. Please choose a plain-text file of email addresses.');
    } finally {
      input.value = '';
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    setResults([]);

    const signature = JSON.stringify([emails, percentOff, duration, durationInMonths, couponName]);
    const batchId = batchIdFor(signature);
    const chunks: string[][] = [];
    for (let i = 0; i < emails.length; i += BATCH_SIZE) {
      chunks.push(emails.slice(i, i + BATCH_SIZE));
    }

    try {
      const collected: CodeResult[] = [];
      for (let i = 0; i < chunks.length; i += 1) {
        setProgress(
          chunks.length > 1
            ? `Creating codes — batch ${i + 1} of ${chunks.length}…`
            : 'Creating codes…'
        );
        const response = await api.post<BulkCouponResponse>('/api/admin/coupons/bulk', {
          emails: chunks[i],
          percentOff,
          duration,
          ...(duration === 'repeating' ? { durationInMonths } : {}),
          ...(couponName.trim() ? { couponName: couponName.trim() } : {}),
          batchId: `${batchId}-c${i}`,
        });
        collected.push(
          ...response.data.results.map((r) => ({ ...r, couponId: response.data.couponId }))
        );
        setResults([...collected]);
      }
    } catch (err) {
      logger.error('Bulk coupon creation failed', err);
      setError(
        'Creating codes failed. Codes listed below were already created; ' +
          'retrying with the same file and settings will not create duplicates.'
      );
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  };

  const handleDownloadCsv = () => {
    const blob = new Blob([buildCouponCsv(results)], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'coupon-codes.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const createdCount = results.filter((r) => r.status === 'created').length;
  const failedCount = results.length - createdCount;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Bulk Stripe Coupon Codes
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload a text file of email addresses to create one single-use Stripe promotion code per
        address. Codes are not emailed automatically — download the CSV and distribute them
        yourself.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
          Choose email list
          <input
            type="file"
            accept=".txt,.csv,text/plain"
            data-testid="coupon-email-file-input"
            onChange={handleFileChange}
            style={{
              clip: 'rect(0 0 0 0)',
              clipPath: 'inset(50%)',
              height: 1,
              overflow: 'hidden',
              position: 'absolute',
              width: 1,
            }}
          />
        </Button>
        {fileName && (
          <Typography variant="body2">
            {emails.length} unique email{emails.length === 1 ? '' : 's'} ready from {fileName}
            {invalidCount > 0 && (
              <Typography component="span" variant="body2" color="warning.main">
                {' '}
                ({invalidCount} invalid entr{invalidCount === 1 ? 'y' : 'ies'} skipped)
              </Typography>
            )}
          </Typography>
        )}
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Percent off"
          type="number"
          value={percentOff}
          onChange={(e) => setPercentOff(Number(e.target.value))}
          inputProps={{ min: 1, max: 100 }}
          sx={{ width: 140 }}
        />
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel id="coupon-duration-label">Duration</InputLabel>
          <Select
            labelId="coupon-duration-label"
            label="Duration"
            value={duration}
            onChange={(e) => setDuration(e.target.value as CouponDuration)}
          >
            <MenuItem value="once">Once</MenuItem>
            <MenuItem value="repeating">Repeating</MenuItem>
            <MenuItem value="forever">Forever</MenuItem>
          </Select>
        </FormControl>
        {duration === 'repeating' && (
          <TextField
            label="Months"
            type="number"
            value={durationInMonths}
            onChange={(e) => setDurationInMonths(Number(e.target.value))}
            inputProps={{ min: 1, max: 36 }}
            sx={{ width: 120 }}
          />
        )}
        <TextField
          label="Coupon name (optional)"
          value={couponName}
          onChange={(e) => setCouponName(e.target.value)}
          inputProps={{ maxLength: 40 }}
          sx={{ minWidth: 220 }}
        />
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            submitting ||
            emails.length === 0 ||
            percentOff < 1 ||
            percentOff > 100 ||
            (duration === 'repeating' && (durationInMonths < 1 || durationInMonths > 36))
          }
        >
          Create codes
        </Button>
        {submitting && (
          <>
            <CircularProgress size={20} />
            {progress && <Typography variant="body2">{progress}</Typography>}
          </>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {results.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">
              {createdCount} code{createdCount === 1 ? '' : 's'} created
              {failedCount > 0 ? `, ${failedCount} failed` : ''}
            </Typography>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownloadCsv}>
              Download CSV
            </Button>
          </Stack>
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((r) => (
                  <TableRow key={r.email}>
                    <TableCell>{r.email}</TableCell>
                    <TableCell>{r.code ?? '—'}</TableCell>
                    <TableCell>{r.status}</TableCell>
                    <TableCell>{r.error ?? ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
